import { isGoogleCalendarSpecialDate } from "@/src/features/integrations/vendors/google/calendarClassification";
import { logSafeError } from "@smartmanager/shell-ui/shared/logging/safeError";

type TaskPriorityNumber = 1 | 2 | 3 | 4 | 5;

export type TaskSourceType =
  | "email"
  | "message"
  | "sms"
  | "video_call"
  | "notes"
  | "manual"
  | "agent"
  | "workflow"
  | "calendar"
  | "unknown";

export type TaskSchedulingMode = "never" | "optional" | "recommended" | "required";
export type TaskScheduleStatus = "unscheduled" | "suggested" | "scheduled" | "completed" | "deferred";
export type TaskTimeBlocking = "flexible" | "specific" | "best_window";
export type TaskDeadlineType = "none" | "soft" | "hard";
export type TaskPreferredTimeOfDay =
  | "early_morning"
  | "morning"
  | "midday"
  | "afternoon"
  | "evening"
  | "late_night"
  | "any";
export type TaskFocusType =
  | "deep_work"
  | "shallow_work"
  | "call"
  | "errand"
  | "travel"
  | "meeting_prep"
  | "admin"
  | "creative"
  | "outdoor"
  | "any";
export type TaskEnergyLevel = "low" | "medium" | "high" | "any";
export type TaskPriorityFramework = "eisenhower" | "ice" | "value_effort" | "moscow" | "rice" | "wsjf";
export type TaskTravelMode = "driving" | "walking" | "transit" | "flight" | "none";
export type TaskOrigin = "user_commitment" | "counterparty_commitment";
export type TaskObligationStatus = "awaiting_counterparty" | "acknowledged";

export function isCounterpartyObligationTask(task: Record<string, any> | null | undefined): boolean {
  const origin = String(task?.origin ?? task?.fetchedData?.origin ?? "").trim().toLowerCase();
  const status = String(task?.obligationStatus ?? task?.fetchedData?.obligationStatus ?? "").trim().toLowerCase();
  return origin === "counterparty_commitment"
    || status === "awaiting_counterparty"
    || status === "acknowledged";
}

/** A party reference keeps the stable contact id when one is known while
 * retaining the source address/name so extraction can remain useful before
 * contact reconciliation has resolved the id. */
export interface TaskPartyReference {
  contactId?: string;
  displayName?: string;
  address?: string;
}

export interface TaskObligationDateAudit {
  previousDueDate?: number | null;
  nextDueDate?: number | null;
  changedAt: number;
  sourceMessageId?: string | null;
  reason?: string | null;
}

export interface TaskDynamicContextRequirements {
  checkTraffic: boolean;
  checkWeather: boolean;
  checkDestinationWeather: boolean;
  checkDocuments: boolean;
  checkContacts: boolean;
}

export interface TaskLocationContext {
  origin: string | null;
  destination: string | null;
  label: string | null;
  mode: TaskTravelMode;
}

export interface TaskLocationDetails {
  provider?: string | null;
  placeId?: string | null;
  id?: string | null;
  name?: string | null;
  formattedAddress?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  googleMapsUri?: string | null;
  types?: string[];
  [key: string]: any;
}

export interface TaskPriorityProfile {
  framework: TaskPriorityFramework;
  priority: TaskPriorityNumber;
  urgency: TaskPriorityNumber;
  importance: TaskPriorityNumber;
  confidence: number;
  effort: TaskPriorityNumber;
  consequenceOfDelay: TaskPriorityNumber;
  businessImpact: TaskPriorityNumber;
  sourceReliability: TaskPriorityNumber;
  rationale?: string;
}

export interface TaskScheduleProfile {
  shouldAppearOnCalendar: boolean;
  schedulingMode: TaskSchedulingMode;
  scheduleStatus: TaskScheduleStatus;
  timeBlocking: TaskTimeBlocking;
  deadlineType: TaskDeadlineType;
  preferredTimeOfDay: TaskPreferredTimeOfDay;
  focusType: TaskFocusType;
  energyLevel: TaskEnergyLevel;
  canSplit: boolean;
  requiresTravel: boolean;
  leadTimeMinutes: number | null;
  earliestStart: number | null;
  latestStart: number | null;
  scheduledStartTime: number | null;
  scheduledEndTime: number | null;
  scheduleConfidence: number;
  scheduleReason?: string;
}

export interface TaskNormalizationInput {
  title?: string | null;
  description?: string | null;
  context?: string | null;
  source?: string | null;
  sourceType?: string | null;
  sourceId?: string | null;
  sourceThreadId?: string | null;
  sourcePlatform?: string | null;
  priority?: number | null;
  urgency?: number | null;
  importance?: number | null;
  confidence?: number | null;
  dueDate?: number | string | null;
  createdDate?: number | string | null;
  expectedDurationMinutes?: number | null;
  timeRequired?: number | null;
  addToCalendar?: boolean | null;
  shouldAppearOnCalendar?: boolean | null;
  schedulingMode?: string | null;
  scheduleStatus?: string | null;
  timeBlocking?: string | null;
  deadlineType?: string | null;
  preferredTimeOfDay?: string | null;
  focusType?: string | null;
  energyLevel?: string | null;
  canSplit?: boolean | null;
  splittable?: boolean | null;
  requiresTravel?: boolean | null;
  leadTimeMinutes?: number | null;
  earliestStart?: number | string | null;
  latestStart?: number | string | null;
  scheduledStartTime?: number | string | null;
  scheduledEndTime?: number | string | null;
  scheduleConfidence?: number | null;
  scheduleReason?: string | null;
  location?: Partial<TaskLocationContext> | null;
  fullAddress?: string | null;
  locationDetails?: TaskLocationDetails | null;
  dynamicContext?: Partial<TaskDynamicContextRequirements> | null;
  priorityProfile?: Partial<TaskPriorityProfile> | null;
  scheduleProfile?: Partial<TaskScheduleProfile> | null;
  fetchedData?: any;
  metadata?: any;
  status?: string | null;
  framework?: string | null;
  reach?: number | null;
  riceReach?: number | null;
  impact?: number | null;
  costOfDelay?: number | null;
  userBusinessValue?: number | null;
  moscowCategory?: string | null;
  origin?: TaskOrigin | string | null;
  promisor?: TaskPartyReference | string | null;
  promisee?: TaskPartyReference | string | null;
  obligationStatus?: TaskObligationStatus | string | null;
  obligationRequiresAcceptance?: boolean | null;
  obligationDateHistory?: TaskObligationDateAudit[] | null;
  category?: string | null;
  sensitive?: boolean | null;
  tags?: string[] | null;
}

export interface CanonicalTaskMetadata {
  sourceType: TaskSourceType;
  sourceId: string | null;
  sourceThreadId: string | null;
  sourcePlatform: string | null;
  estimatedDurationMinutes: number;
  location: TaskLocationContext;
  fullAddress: string | null;
  locationDetails: TaskLocationDetails | null;
  dynamicContext: TaskDynamicContextRequirements;
  priorityProfile: TaskPriorityProfile;
  scheduleProfile: TaskScheduleProfile;
}

function normalizePartyReference(value: unknown): TaskPartyReference | undefined {
  if (typeof value === "string") {
    const address = value.trim();
    return address ? { address } : undefined;
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const record = value as Record<string, unknown>;
  const contactId = normalizeString(record.contactId ?? record.id);
  const displayName = normalizeString(record.displayName ?? record.name ?? record.fullName);
  const address = normalizeString(record.address ?? record.email ?? record.value);
  if (!contactId && !displayName && !address) return undefined;
  return {
    ...(contactId ? { contactId } : {}),
    ...(displayName ? { displayName } : {}),
    ...(address ? { address } : {}),
  };
}

function normalizeObligationStatus(value: unknown): TaskObligationStatus | undefined {
  const status = normalizeString(value).toLowerCase();
  return status === "awaiting_counterparty" || status === "acknowledged"
    ? status
    : undefined;
}

function normalizeDateHistory(value: unknown): TaskObligationDateAudit[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return [];
    const record = entry as Record<string, unknown>;
    const changedAtValue = typeof record.changedAt === "number" || typeof record.changedAt === "string"
      ? record.changedAt
      : null;
    const previousDueDateValue = typeof record.previousDueDate === "number" || typeof record.previousDueDate === "string"
      ? record.previousDueDate
      : null;
    const nextDueDateValue = typeof record.nextDueDate === "number" || typeof record.nextDueDate === "string"
      ? record.nextDueDate
      : null;
    const changedAt = asEpoch(changedAtValue);
    if (!changedAt) return [];
    return [{
      previousDueDate: asEpoch(previousDueDateValue),
      nextDueDate: asEpoch(nextDueDateValue),
      changedAt,
      sourceMessageId: coalesceString(record.sourceMessageId),
      reason: coalesceString(record.reason),
    }];
  });
}

const EARLY_MORNING_KEYWORDS = /\b(early morning|first thing|sunrise|before school|before work)\b/i;
const MORNING_KEYWORDS = /\b(morning|breakfast|before noon|9 ?am|10 ?am|11 ?am)\b/i;
const MIDDAY_KEYWORDS = /\b(midday|noon|lunch|lunchtime|12 ?pm)\b/i;
const AFTERNOON_KEYWORDS = /\b(afternoon|after lunch|2 ?pm|3 ?pm|4 ?pm|5 ?pm)\b/i;
const EVENING_KEYWORDS = /\b(evening|tonight|after dinner|6 ?pm|7 ?pm|8 ?pm)\b/i;
const LATE_NIGHT_KEYWORDS = /\b(late night|night shift|before bed|9 ?pm|10 ?pm|11 ?pm)\b/i;

const CALL_KEYWORDS = /\b(call|phone|ring|text|message|reply|respond|follow up)\b/i;
const ERRAND_KEYWORDS = /\b(pick up|drop off|grocery|groceries|store|bank|errand|pharmacy|mail|post office|buy)\b/i;
const TRAVEL_KEYWORDS = /\b(travel|drive|commute|airport|flight|trip|route|traffic|departure|arrive)\b/i;
const OUTDOOR_KEYWORDS = /\b(outdoor|outside|hike|walk|field|practice|soccer|game|weather|rain|snow|umbrella)\b/i;
const DEEP_WORK_KEYWORDS = /\b(review|write|draft|prepare|analyze|research|design|plan|proposal|strategy|presentation|study)\b/i;
const DOCUMENT_KEYWORDS = /\b(document|contract|proposal|slide|presentation|report|spreadsheet|invoice|application|form)\b/i;
const CONTACT_KEYWORDS = /\b(call|email|reply|follow up|schedule with|coordinate|meet|interview)\b/i;
const HARD_DEADLINE_KEYWORDS = /\b(must|required|deadline|due by|submit by|before|no later than|final notice|overdue)\b/i;

const SOURCE_RELIABILITY: Record<TaskSourceType, TaskPriorityNumber> = {
  manual: 1,
  email: 2,
  notes: 2,
  message: 2,
  sms: 2,
  video_call: 3,
  agent: 3,
  workflow: 3,
  calendar: 3,
  unknown: 4,
};

function clampPriority(value: number | null | undefined, fallback: TaskPriorityNumber = 3): TaskPriorityNumber {
  // Number(null) and Number("") are 0, which would clamp to priority 1: a
  // missing priority would read as the most urgent one.
  if (value === null || value === undefined || (typeof value === "string" && !(value as string).trim())) return fallback;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  const clamped = Math.min(5, Math.max(1, Math.round(numeric)));
  return clamped as TaskPriorityNumber;
}

function clampConfidence(value: number | null | undefined, fallback = 0.8): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.min(1, Math.max(0, numeric));
}

function asEpoch(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 10_000_000) return numeric;
  const parsed = new Date(String(value)).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeString(value: unknown): string {
  return String(value || "").trim();
}

function coalesceString(...values: unknown[]): string | null {
  for (const value of values) {
    const normalized = normalizeString(value);
    if (normalized) return normalized;
  }
  return null;
}

function parseLocationCandidate(value: unknown): Partial<TaskLocationContext> | string | null {
  if (!value) return null;
  if (typeof value === "object" && !Array.isArray(value)) {
    return value as Partial<TaskLocationContext>;
  }
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Partial<TaskLocationContext>;
      }
    } catch {
      // Fall back to treating the value as plain text.
    }
  }

  return trimmed;
}

export function parseNestedFetchedData(input: any): Record<string, any> {
  try {
    if (!input?.fetchedData) return {};
    if (input.fetchedData?.data && typeof input.fetchedData.data === "string") {
      return JSON.parse(input.fetchedData.data);
    }
    if (typeof input.fetchedData === "string") {
      return JSON.parse(input.fetchedData);
    }
    if (typeof input.fetchedData === "object") {
      return input.fetchedData;
    }
  } catch (error) {
    console.warn("[taskModel] Failed to parse fetchedData:", logSafeError(error));
  }
  return {};
}

function normalizeSourceType(sourceType: unknown, source: unknown, fetchedData: Record<string, any>): TaskSourceType {
  const raw = normalizeString(sourceType || source || fetchedData.source || fetchedData.sourceType).toLowerCase();
  if (!raw) return "unknown";
  if (raw.includes("email")) return "email";
  if (raw === "sms") return "sms";
  if (raw.includes("message") || raw.includes("slack") || raw.includes("teams") || raw.includes("discord")) return "message";
  if (raw.includes("video") || raw.includes("meeting") || raw.includes("transcript") || raw.includes("phone")) return "video_call";
  if (raw.includes("note")) return "notes";
  if (raw.includes("workflow")) return "workflow";
  if (raw.includes("agent") || raw.includes("proactive")) return "agent";
  if (raw.includes("manual")) return "manual";
  if (raw.includes("calendar")) return "calendar";
  return "unknown";
}

function inferPreferredTimeOfDay(text: string, durationMinutes: number, provided?: string | null): TaskPreferredTimeOfDay {
  const raw = normalizeString(provided).toLowerCase();
  if (
    raw === "early_morning" ||
    raw === "morning" ||
    raw === "midday" ||
    raw === "afternoon" ||
    raw === "evening" ||
    raw === "late_night" ||
    raw === "any"
  ) {
    return raw as TaskPreferredTimeOfDay;
  }

  if (EARLY_MORNING_KEYWORDS.test(text)) return "early_morning";
  if (MORNING_KEYWORDS.test(text)) return "morning";
  if (MIDDAY_KEYWORDS.test(text)) return "midday";
  if (AFTERNOON_KEYWORDS.test(text)) return "afternoon";
  if (EVENING_KEYWORDS.test(text)) return "evening";
  if (LATE_NIGHT_KEYWORDS.test(text)) return "late_night";
  if (durationMinutes >= 90 && DEEP_WORK_KEYWORDS.test(text)) return "morning";
  return "any";
}

function inferFocusType(text: string, durationMinutes: number, provided?: string | null): TaskFocusType {
  const raw = normalizeString(provided).toLowerCase();
  if (
    raw === "deep_work" ||
    raw === "shallow_work" ||
    raw === "call" ||
    raw === "errand" ||
    raw === "travel" ||
    raw === "meeting_prep" ||
    raw === "admin" ||
    raw === "creative" ||
    raw === "outdoor" ||
    raw === "any"
  ) {
    return raw as TaskFocusType;
  }

  if (CALL_KEYWORDS.test(text)) return "call";
  if (ERRAND_KEYWORDS.test(text)) return "errand";
  if (TRAVEL_KEYWORDS.test(text)) return "travel";
  if (OUTDOOR_KEYWORDS.test(text)) return "outdoor";
  if (text.match(/\b(prepare|prep|briefing|agenda)\b/i)) return "meeting_prep";
  if (text.match(/\b(invoice|expense|admin|paperwork|form)\b/i)) return "admin";
  if (text.match(/\b(brainstorm|design|creative|outline)\b/i)) return "creative";
  if (durationMinutes >= 75 || DEEP_WORK_KEYWORDS.test(text)) return "deep_work";
  return "shallow_work";
}

function inferEnergyLevel(focusType: TaskFocusType, durationMinutes: number, provided?: string | null): TaskEnergyLevel {
  const raw = normalizeString(provided).toLowerCase();
  if (raw === "low" || raw === "medium" || raw === "high" || raw === "any") {
    return raw as TaskEnergyLevel;
  }
  if (focusType === "deep_work" || focusType === "creative") return durationMinutes >= 120 ? "high" : "medium";
  if (focusType === "errand" || focusType === "admin") return "low";
  if (focusType === "travel" || focusType === "outdoor") return "medium";
  return "any";
}

function inferLocation(input: TaskNormalizationInput, fetchedData: Record<string, any>, focusType: TaskFocusType): TaskLocationContext {
  const primaryLocation = parseLocationCandidate(input.location || fetchedData.location || fetchedData.taskLocation);
  const fetchedLocation = parseLocationCandidate(fetchedData.location);
  const locationObject =
    (primaryLocation && typeof primaryLocation === "object" ? primaryLocation : null) ||
    (fetchedLocation && typeof fetchedLocation === "object" ? fetchedLocation : null) ||
    {};
  const locationText =
    (typeof primaryLocation === "string" ? primaryLocation : null) ||
    (typeof fetchedLocation === "string" ? fetchedLocation : null);
  const label = coalesceString(
    (locationObject as any)?.label,
    locationText,
    fetchedData.destination,
    fetchedData.address,
  );
  const destination = coalesceString(
    (locationObject as any)?.destination,
    locationText,
    fetchedData.destination,
    fetchedData.address,
  );
  const origin = coalesceString((locationObject as any)?.origin, fetchedData.origin, fetchedData.fromLocation);
  const rawMode = normalizeString((locationObject as any)?.mode || fetchedData.travelMode).toLowerCase();
  const mode: TaskTravelMode =
    rawMode === "driving" || rawMode === "walking" || rawMode === "transit" || rawMode === "flight"
      ? (rawMode as TaskTravelMode)
      : focusType === "travel" || destination
        ? "driving"
        : "none";

  return {
    origin: origin || null,
    destination: destination || null,
    label: label || null,
    mode,
  };
}

function inferLocationDetails(input: TaskNormalizationInput, fetchedData: Record<string, any>): TaskLocationDetails | null {
  const details =
    (input.locationDetails && typeof input.locationDetails === "object" ? input.locationDetails : null) ||
    (fetchedData.locationDetails && typeof fetchedData.locationDetails === "object" ? fetchedData.locationDetails : null) ||
    null;

  if (!details) return null;
  return { ...details };
}

function inferFullAddress(
  input: TaskNormalizationInput,
  fetchedData: Record<string, any>,
  locationDetails: TaskLocationDetails | null,
): string | null {
  return coalesceString(
    input.fullAddress,
    locationDetails?.formattedAddress,
    fetchedData.fullAddress,
    fetchedData.formattedAddress,
  );
}

function inferDynamicContext(
  input: TaskNormalizationInput,
  fetchedData: Record<string, any>,
  text: string,
  location: TaskLocationContext,
  focusType: TaskFocusType,
): TaskDynamicContextRequirements {
  const provided = input.dynamicContext || {};
  const checkTraffic = Boolean(
    provided.checkTraffic ??
      fetchedData.checkTraffic ??
      input.requiresTravel ??
      location.destination ??
      (focusType === "travel"),
  );
  const derivedTraffic = checkTraffic || ERRAND_KEYWORDS.test(text) || TRAVEL_KEYWORDS.test(text);
  const checkWeather = Boolean(
    provided.checkWeather ??
      fetchedData.checkWeather ??
      (focusType === "outdoor"),
  );
  const derivedWeather = checkWeather || OUTDOOR_KEYWORDS.test(text);
  const checkDestinationWeather = Boolean(
    provided.checkDestinationWeather ??
      fetchedData.checkDestinationWeather ??
      (focusType === "travel" && Boolean(location.destination)),
  );
  const checkDocuments = Boolean(
    provided.checkDocuments ?? fetchedData.checkDocuments ?? DOCUMENT_KEYWORDS.test(text),
  );
  const checkContacts = Boolean(
    provided.checkContacts ?? fetchedData.checkContacts ?? CONTACT_KEYWORDS.test(text),
  );

  return {
    checkTraffic: derivedTraffic,
    checkWeather: derivedWeather,
    checkDestinationWeather,
    checkDocuments,
    checkContacts,
  };
}

function inferUrgency(input: TaskNormalizationInput, now: number, fallbackPriority: TaskPriorityNumber): TaskPriorityNumber {
  const provided = input.priorityProfile?.urgency ?? input.urgency;
  if (provided !== undefined && provided !== null) return clampPriority(provided, fallbackPriority);

  const dueDate = asEpoch(input.dueDate);
  if (!dueDate) return fallbackPriority;
  const diff = dueDate - now;
  if (diff <= 4 * 60 * 60 * 1000) return 1;
  if (diff <= 24 * 60 * 60 * 1000) return 2;
  if (diff <= 3 * 24 * 60 * 60 * 1000) return 3;
  if (diff <= 7 * 24 * 60 * 60 * 1000) return 4;
  return 5;
}

function inferImportance(
  input: TaskNormalizationInput,
  text: string,
  sourceType: TaskSourceType,
  fallbackPriority: TaskPriorityNumber,
): TaskPriorityNumber {
  const provided = input.priorityProfile?.importance ?? input.importance;
  if (provided !== undefined && provided !== null) return clampPriority(provided, fallbackPriority);

  if (HARD_DEADLINE_KEYWORDS.test(text)) return Math.min(fallbackPriority, 2) as TaskPriorityNumber;
  if (sourceType === "manual") return Math.min(fallbackPriority, 2) as TaskPriorityNumber;
  if (sourceType === "email" && DOCUMENT_KEYWORDS.test(text)) return Math.min(fallbackPriority, 2) as TaskPriorityNumber;
  return fallbackPriority;
}

function durationToEffort(durationMinutes: number): TaskPriorityNumber {
  if (durationMinutes <= 20) return 1;
  if (durationMinutes <= 45) return 2;
  if (durationMinutes <= 90) return 3;
  if (durationMinutes <= 150) return 4;
  return 5;
}

function selectPriorityFramework(input: TaskNormalizationInput, importance: TaskPriorityNumber, urgency: TaskPriorityNumber): TaskPriorityFramework {
  const raw = normalizeString(input.framework || input.priorityProfile?.framework).toLowerCase();
  if (raw === "eisenhower" || raw === "ice" || raw === "value_effort" || raw === "moscow" || raw === "rice" || raw === "wsjf") {
    return raw as TaskPriorityFramework;
  }
  if (input.moscowCategory) return "moscow";
  if (input.reach || input.riceReach) return "rice";
  if (input.costOfDelay || input.userBusinessValue) return "wsjf";
  if (importance && urgency) return "eisenhower";
  if (input.confidence !== undefined || input.priorityProfile?.confidence !== undefined) return "ice";
  return "value_effort";
}

export function buildCanonicalTaskMetadata(input: TaskNormalizationInput): CanonicalTaskMetadata {
  const fetchedData = parseNestedFetchedData(input);
  const title = normalizeString(input.title || fetchedData.title);
  const description = normalizeString(input.description || fetchedData.description);
  const context = normalizeString(input.context || fetchedData.context);
  const text = `${title}\n${description}\n${context}`;
  const sourceType = normalizeSourceType(input.sourceType, input.source, fetchedData);
  const estimatedDurationMinutes = Math.max(
    15,
    Math.round(
      Number(
        input.expectedDurationMinutes ??
          input.timeRequired ??
          fetchedData.expectedDurationMinutes ??
          fetchedData.timeRequired ??
          30,
      ) || 30,
    ),
  );
  const basePriority = clampPriority(input.priority ?? input.priorityProfile?.priority ?? fetchedData.priority, 3);
  const now = Date.now();
  const urgency = inferUrgency(input, now, basePriority);
  const importance = inferImportance(input, text, sourceType, basePriority);
  const framework = selectPriorityFramework(input, importance, urgency);
  const focusType = inferFocusType(text, estimatedDurationMinutes, input.focusType ?? input.scheduleProfile?.focusType);
  const preferredTimeOfDay = inferPreferredTimeOfDay(
    text,
    estimatedDurationMinutes,
    input.preferredTimeOfDay ?? input.scheduleProfile?.preferredTimeOfDay,
  );
  const energyLevel = inferEnergyLevel(
    focusType,
    estimatedDurationMinutes,
    input.energyLevel ?? input.scheduleProfile?.energyLevel,
  );
  const location = inferLocation(input, fetchedData, focusType);
  const locationDetails = inferLocationDetails(input, fetchedData);
  const fullAddress = inferFullAddress(input, fetchedData, locationDetails);
  const dynamicContext = inferDynamicContext(input, fetchedData, text, location, focusType);
  const dueDate = asEpoch(input.dueDate);
  const scheduledStartTime = asEpoch(input.scheduledStartTime ?? input.scheduleProfile?.scheduledStartTime ?? fetchedData.scheduledStartTime);
  const scheduledEndTime = asEpoch(input.scheduledEndTime ?? input.scheduleProfile?.scheduledEndTime ?? fetchedData.scheduledEndTime);
  const canSplit = Boolean(input.canSplit ?? input.splittable ?? input.scheduleProfile?.canSplit ?? estimatedDurationMinutes >= 90);
  const requiresTravel = Boolean(
    input.requiresTravel ??
      input.scheduleProfile?.requiresTravel ??
      location.destination ??
      (focusType === "travel"),
  );
  const derivedRequiresTravel = requiresTravel || focusType === "errand";
  const timeBlockingRaw = normalizeString(input.timeBlocking ?? input.scheduleProfile?.timeBlocking ?? fetchedData.timeBlocking).toLowerCase();
  const timeBlocking: TaskTimeBlocking =
    timeBlockingRaw === "specific" || timeBlockingRaw === "best_window"
      ? (timeBlockingRaw as TaskTimeBlocking)
      : dynamicContext.checkTraffic || dynamicContext.checkWeather
        ? "best_window"
        : "flexible";

  const deadlineTypeRaw = normalizeString(input.deadlineType ?? input.scheduleProfile?.deadlineType).toLowerCase();
  const deadlineType: TaskDeadlineType =
    deadlineTypeRaw === "hard" || deadlineTypeRaw === "soft"
      ? (deadlineTypeRaw as TaskDeadlineType)
      : dueDate && HARD_DEADLINE_KEYWORDS.test(text)
        ? "hard"
        : dueDate
          ? "soft"
          : "none";

  // `shouldAppearOnCalendar` is the CANONICAL field; `addToCalendar` is a
  // legacy alias accepted on input for old writers/documents and re-derived
  // from shouldAppearOnCalendar on every write. Never set the two
  // independently — new code should read/write shouldAppearOnCalendar only.
  const explicitShouldAppear = input.shouldAppearOnCalendar ?? input.addToCalendar ?? input.scheduleProfile?.shouldAppearOnCalendar;
  const shouldAppearOnCalendar = Boolean(
    explicitShouldAppear ??
      scheduledStartTime ??
      (timeBlocking !== "flexible"),
  );
  const derivedShouldAppearOnCalendar =
    shouldAppearOnCalendar ||
    estimatedDurationMinutes >= 45 ||
    derivedRequiresTravel ||
    preferredTimeOfDay !== "any" ||
    focusType === "deep_work";

  const schedulingModeRaw = normalizeString(input.schedulingMode ?? input.scheduleProfile?.schedulingMode).toLowerCase();
  let schedulingMode: TaskSchedulingMode;
  if (schedulingModeRaw === "never" || schedulingModeRaw === "optional" || schedulingModeRaw === "recommended" || schedulingModeRaw === "required") {
    schedulingMode = schedulingModeRaw as TaskSchedulingMode;
  } else if (scheduledStartTime || timeBlocking === "specific") {
    schedulingMode = "required";
  } else if (derivedShouldAppearOnCalendar || urgency <= 2 || importance <= 2) {
    schedulingMode = "recommended";
  } else {
    schedulingMode = "optional";
  }

  const scheduleStatusRaw = normalizeString(input.scheduleStatus ?? input.scheduleProfile?.scheduleStatus).toLowerCase();
  let scheduleStatus: TaskScheduleStatus;
  if (
    scheduleStatusRaw === "unscheduled" ||
    scheduleStatusRaw === "suggested" ||
    scheduleStatusRaw === "scheduled" ||
    scheduleStatusRaw === "completed" ||
    scheduleStatusRaw === "deferred"
  ) {
    scheduleStatus = scheduleStatusRaw as TaskScheduleStatus;
  } else if (input.status === "completed") {
    scheduleStatus = "completed";
  } else if (input.status === "deferred") {
    scheduleStatus = "deferred";
  } else if (scheduledStartTime && scheduledEndTime) {
    scheduleStatus = "scheduled";
  } else {
    scheduleStatus = "unscheduled";
  }

  const confidence = clampConfidence(input.priorityProfile?.confidence ?? input.confidence ?? fetchedData.confidence, 0.85);
  const priorityProfile: TaskPriorityProfile = {
    framework,
    priority: basePriority,
    urgency,
    importance,
    confidence,
    effort: clampPriority(input.priorityProfile?.effort ?? durationToEffort(estimatedDurationMinutes), durationToEffort(estimatedDurationMinutes)),
    consequenceOfDelay: clampPriority(
      input.priorityProfile?.consequenceOfDelay ??
        input.costOfDelay ??
        (dueDate && dueDate - now <= 24 * 60 * 60 * 1000 ? 1 : basePriority),
      basePriority,
    ),
    businessImpact: clampPriority(input.priorityProfile?.businessImpact ?? input.userBusinessValue ?? importance, importance),
    sourceReliability: clampPriority(input.priorityProfile?.sourceReliability ?? SOURCE_RELIABILITY[sourceType], SOURCE_RELIABILITY[sourceType]),
    rationale: coalesceString(input.priorityProfile?.rationale, fetchedData.priorityReason, fetchedData.reason) || undefined,
  };

  const earliestStart = asEpoch(input.earliestStart ?? input.scheduleProfile?.earliestStart ?? fetchedData.earliestStart);
  const latestStart = asEpoch(input.latestStart ?? input.scheduleProfile?.latestStart ?? dueDate ?? fetchedData.latestStart);
  const scheduleConfidence = clampConfidence(
    input.scheduleConfidence ?? input.scheduleProfile?.scheduleConfidence ?? fetchedData.scheduleConfidence,
    confidence,
  );
  const scheduleProfile: TaskScheduleProfile = {
    shouldAppearOnCalendar: derivedShouldAppearOnCalendar,
    schedulingMode,
    scheduleStatus,
    timeBlocking,
    deadlineType,
    preferredTimeOfDay,
    focusType,
    energyLevel,
    canSplit,
    requiresTravel: derivedRequiresTravel,
    leadTimeMinutes: Number(input.leadTimeMinutes ?? input.scheduleProfile?.leadTimeMinutes ?? fetchedData.leadTimeMinutes) || null,
    earliestStart,
    latestStart,
    scheduledStartTime,
    scheduledEndTime,
    scheduleConfidence,
    scheduleReason: coalesceString(
      input.scheduleReason,
      input.scheduleProfile?.scheduleReason,
      fetchedData.scheduleReason,
      timeBlocking === "best_window" ? "Dynamic context may change the best time for this task." : null,
    ) || undefined,
  };

  return {
    sourceType,
    sourceId: coalesceString(input.sourceId, fetchedData.sourceId, fetchedData.emailId, fetchedData.docId),
    sourceThreadId: coalesceString(input.sourceThreadId, fetchedData.sourceThreadId, fetchedData.threadId),
    sourcePlatform: coalesceString(input.sourcePlatform, fetchedData.sourcePlatform, fetchedData.vendor, fetchedData.platform),
    estimatedDurationMinutes,
    location,
    fullAddress,
    locationDetails,
    dynamicContext,
    priorityProfile,
    scheduleProfile,
  };
}

export function buildTaskCompatibilityPayload(input: TaskNormalizationInput, metadata: CanonicalTaskMetadata): Record<string, any> {
  const fetchedData = parseNestedFetchedData(input);
  const origin = normalizeString(input.origin ?? fetchedData.origin);
  const promisor = normalizePartyReference(input.promisor ?? fetchedData.promisor);
  const promisee = normalizePartyReference(input.promisee ?? fetchedData.promisee);
  const obligationStatus = normalizeObligationStatus(input.obligationStatus ?? fetchedData.obligationStatus);
  const obligationDateHistory = normalizeDateHistory(input.obligationDateHistory ?? fetchedData.obligationDateHistory);
  const category = normalizeString(input.category ?? fetchedData.category);
  const sensitive = input.sensitive ?? fetchedData.sensitive;
  const tags = Array.isArray(input.tags ?? fetchedData.tags)
    ? (input.tags ?? fetchedData.tags).filter((value: unknown): value is string => typeof value === "string")
    : [];
  return {
    ...fetchedData,
    source: metadata.sourceType,
    sourceType: metadata.sourceType,
    sourceId: metadata.sourceId,
    sourceThreadId: metadata.sourceThreadId,
    sourcePlatform: metadata.sourcePlatform,
    expectedDurationMinutes: metadata.estimatedDurationMinutes,
    timeBlocking: metadata.scheduleProfile.timeBlocking,
    addToCalendar: metadata.scheduleProfile.shouldAppearOnCalendar,
    shouldAppearOnCalendar: metadata.scheduleProfile.shouldAppearOnCalendar,
    schedulingMode: metadata.scheduleProfile.schedulingMode,
    scheduleStatus: metadata.scheduleProfile.scheduleStatus,
    deadlineType: metadata.scheduleProfile.deadlineType,
    preferredTimeOfDay: metadata.scheduleProfile.preferredTimeOfDay,
    focusType: metadata.scheduleProfile.focusType,
    energyLevel: metadata.scheduleProfile.energyLevel,
    canSplit: metadata.scheduleProfile.canSplit,
    requiresTravel: metadata.scheduleProfile.requiresTravel,
    leadTimeMinutes: metadata.scheduleProfile.leadTimeMinutes,
    earliestStart: metadata.scheduleProfile.earliestStart,
    latestStart: metadata.scheduleProfile.latestStart,
    scheduledStartTime: metadata.scheduleProfile.scheduledStartTime,
    scheduledEndTime: metadata.scheduleProfile.scheduledEndTime,
    scheduleConfidence: metadata.scheduleProfile.scheduleConfidence,
    scheduleReason: metadata.scheduleProfile.scheduleReason,
    location: metadata.location,
    fullAddress: metadata.fullAddress,
    locationDetails: metadata.locationDetails,
    dynamicContext: metadata.dynamicContext,
    priorityProfile: metadata.priorityProfile,
    scheduleProfile: metadata.scheduleProfile,
    ...(origin ? { origin } : {}),
    ...(promisor ? { promisor } : {}),
    ...(promisee ? { promisee } : {}),
    ...(obligationStatus ? { obligationStatus } : {}),
    ...(input.obligationRequiresAcceptance ?? fetchedData.obligationRequiresAcceptance) !== undefined
      ? { obligationRequiresAcceptance: Boolean(input.obligationRequiresAcceptance ?? fetchedData.obligationRequiresAcceptance) }
      : {},
    ...(obligationDateHistory.length > 0 ? { obligationDateHistory } : {}),
    ...(category ? { category } : {}),
    ...(typeof sensitive === "boolean" ? { sensitive } : {}),
    ...(tags.length > 0 ? { tags } : {}),
  };
}

export function normalizeUnifiedTaskDoc(raw: Record<string, any>): Record<string, any> {
  const fetchedData = parseNestedFetchedData(raw);
  const origin = normalizeString(raw.origin ?? fetchedData.origin);
  const promisor = normalizePartyReference(raw.promisor ?? fetchedData.promisor);
  const promisee = normalizePartyReference(raw.promisee ?? fetchedData.promisee);
  const obligationStatus = normalizeObligationStatus(raw.obligationStatus ?? fetchedData.obligationStatus);
  const obligationRequiresAcceptance = raw.obligationRequiresAcceptance ?? fetchedData.obligationRequiresAcceptance;
  const obligationDateHistory = normalizeDateHistory(raw.obligationDateHistory ?? fetchedData.obligationDateHistory);
  const metadata = buildCanonicalTaskMetadata({
    ...raw,
    fetchedData,
    expectedDurationMinutes:
      raw.expectedDurationMinutes ??
      raw.timeRequired ??
      fetchedData.expectedDurationMinutes ??
      fetchedData.timeRequired,
    addToCalendar:
      raw.addToCalendar ??
      raw.shouldAppearOnCalendar ??
      fetchedData.addToCalendar ??
      fetchedData.shouldAppearOnCalendar,
    timeBlocking: raw.timeBlocking ?? fetchedData.timeBlocking,
    schedulingMode: raw.schedulingMode ?? fetchedData.schedulingMode,
    scheduleStatus: raw.scheduleStatus ?? fetchedData.scheduleStatus,
    deadlineType: raw.deadlineType ?? fetchedData.deadlineType,
    preferredTimeOfDay: raw.preferredTimeOfDay ?? fetchedData.preferredTimeOfDay,
    focusType: raw.focusType ?? fetchedData.focusType,
    energyLevel: raw.energyLevel ?? fetchedData.energyLevel,
    location: raw.location ?? fetchedData.location,
    fullAddress: raw.fullAddress ?? fetchedData.fullAddress,
    locationDetails: raw.locationDetails ?? fetchedData.locationDetails,
    dynamicContext: raw.dynamicContext ?? fetchedData.dynamicContext,
    priorityProfile: raw.priorityProfile ?? fetchedData.priorityProfile,
    scheduleProfile: raw.scheduleProfile ?? fetchedData.scheduleProfile,
    origin: raw.origin ?? fetchedData.origin,
    promisor: raw.promisor ?? fetchedData.promisor,
    promisee: raw.promisee ?? fetchedData.promisee,
    obligationStatus: raw.obligationStatus ?? fetchedData.obligationStatus,
    obligationRequiresAcceptance: raw.obligationRequiresAcceptance ?? fetchedData.obligationRequiresAcceptance,
    obligationDateHistory: raw.obligationDateHistory ?? fetchedData.obligationDateHistory,
  });
  const normalizedType = isGoogleCalendarSpecialDate({
    type: raw.type,
    vendor: raw.vendor,
    sourcePlatform: raw.sourcePlatform ?? fetchedData.sourcePlatform ?? fetchedData.vendor,
    eventType: raw.eventType ?? fetchedData.eventType,
    birthdayProperties: raw.birthdayProperties ?? fetchedData.birthdayProperties,
    title: raw.title,
    summary: raw.summary,
    description: raw.description,
    isAllDay: raw.isAllDay ?? fetchedData.isAllDay,
    attendees: raw.attendees ?? fetchedData.attendees,
    location: raw.location ?? fetchedData.location,
    conferenceData: raw.conferenceData ?? fetchedData.conferenceData,
    hangoutLink: raw.hangoutLink ?? fetchedData.hangoutLink,
  })
    ? "reminder"
    : raw.type;

  return {
    ...raw,
    type: normalizedType,
    estimatedDurationMinutes: metadata.estimatedDurationMinutes,
    sourceType: metadata.sourceType,
    sourceId: metadata.sourceId,
    sourceThreadId: metadata.sourceThreadId,
    sourcePlatform: metadata.sourcePlatform,
    timeBlocking: metadata.scheduleProfile.timeBlocking,
    addToCalendar: metadata.scheduleProfile.shouldAppearOnCalendar,
    shouldAppearOnCalendar: metadata.scheduleProfile.shouldAppearOnCalendar,
    schedulingMode: metadata.scheduleProfile.schedulingMode,
    scheduleStatus: metadata.scheduleProfile.scheduleStatus,
    deadlineType: metadata.scheduleProfile.deadlineType,
    preferredTimeOfDay: metadata.scheduleProfile.preferredTimeOfDay,
    focusType: metadata.scheduleProfile.focusType,
    energyLevel: metadata.scheduleProfile.energyLevel,
    canSplit: metadata.scheduleProfile.canSplit,
    requiresTravel: metadata.scheduleProfile.requiresTravel,
    leadTimeMinutes: metadata.scheduleProfile.leadTimeMinutes,
    earliestStart: metadata.scheduleProfile.earliestStart,
    latestStart: metadata.scheduleProfile.latestStart,
    scheduledStartTime: metadata.scheduleProfile.scheduledStartTime,
    scheduledEndTime: metadata.scheduleProfile.scheduledEndTime,
    scheduleConfidence: metadata.scheduleProfile.scheduleConfidence,
    scheduleReason: metadata.scheduleProfile.scheduleReason,
    location: metadata.location,
    fullAddress: metadata.fullAddress,
    locationDetails: metadata.locationDetails,
    dynamicContext: metadata.dynamicContext,
    priorityProfile: metadata.priorityProfile,
    scheduleProfile: metadata.scheduleProfile,
    ...(origin ? { origin } : {}),
    ...(promisor ? { promisor } : {}),
    ...(promisee ? { promisee } : {}),
    ...(obligationStatus ? { obligationStatus } : {}),
    ...(obligationRequiresAcceptance !== undefined
      ? { obligationRequiresAcceptance: Boolean(obligationRequiresAcceptance) }
      : {}),
    ...(obligationDateHistory.length > 0 ? { obligationDateHistory } : {}),
    fetchedData: buildTaskCompatibilityPayload(raw, metadata),
  };
}

export function priorityNumberToUrgency(priority?: number | null): "critical" | "high" | "medium" | "low" {
  const normalized = clampPriority(priority, 3);
  if (normalized === 1) return "critical";
  if (normalized === 2) return "high";
  if (normalized === 3) return "medium";
  return "low";
}

export function invertTaskPriority(priority?: number | null): number {
  const normalized = clampPriority(priority, 3);
  return 6 - normalized;
}

export function isHighTaskPriority(priority?: number | null): boolean {
  return clampPriority(priority, 3) <= 2;
}
